import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Switch,
  Tag
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { FollowUpStatus } from '../../../types/followup';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { TextArea } = Input;

const FollowUpStatusList: React.FC = () => {
  const [statusList, setStatusList] = useState<FollowUpStatus[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStatus, setEditingStatus] = useState<FollowUpStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadFUStatus.create');
  const canUpdate = hasPermission('leadFUStatus.update');
  const canDelete = hasPermission('leadFUStatus.delete');

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-follow-up-status');
      if (response.data && response.data.status === 'success') {
        setStatusList(response.data.data);
      } else {
        setStatusList([]);
        message.error('Gagal memuat data status follow-up');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Gagal mengambil data status follow-up';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const showModal = (status: FollowUpStatus | null = null) => {
    setEditingStatus(status);
    if (status) {
      form.setFieldsValue({
        name: status.name,
        description: status.description,
        is_default: status.is_default,
      });
    } else {
      form.setFieldsValue({
        name: '',
        description: '',
        is_default: false,
      });
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingStatus(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        name: values.name,
        description: values.description,
        is_default: values.is_default || false,
      };

      if (editingStatus) {
        await api.put(`lead-follow-up-status/${editingStatus.id}`, payload);
        message.success('Status Follow Up berhasil diperbarui');
      } else {
        await api.post('lead-follow-up-status', payload);
        message.success('Status Follow Up berhasil ditambahkan');
      }

      handleCancel();
      fetchStatuses(); // Refresh data
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Terjadi kesalahan. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: FollowUpStatus) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivate' : 'reactivate';
      const endpoint = record.is_active
        ? `/lead-follow-up-status/${record.id}`
        : `/lead-follow-up-status/${record.id}/reactivate`;

        if (record.is_active) {
          await api.delete(endpoint);
        } else {
          await api.put(endpoint);
        }

        message.success(`Status Follow Up ${action} berhasil`);
        fetchStatuses();
    } catch (error: any) {
      const action = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Gagal ${action} status Follow Up`);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nama Status Follow Up',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Deskripsi',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: FollowUpStatus) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Apakah Anda yakin ingin ${
                record.is_active ? 'deactivate' : 'reactivate'
              } status ini?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Ya"
              cancelText="Tidak"
              disabled={record.is_default}
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                disabled={record.is_default}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Follow Up Status Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Status Follow Up
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={statusList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingStatus ? 'Edit Status Follow Up' : 'Tambah Status Follow Up Baru'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="followUpStatusForm">
          <Form.Item
            name="name"
            label="Nama Status"
            rules={[{ required: true, message: 'Nama status tidak boleh kosong!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Deskripsi"
          >
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="is_default"
            label="Jadikan Default"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FollowUpStatusList;
